import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = [
  "super_admin",
  "owner",
  "manager",
  "cashier",
  "accountant",
  "karigar",
  "sales",
];

export default function Users() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    role: "cashier",
    is_active: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setList(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      email: "",
      mobile: "",
      password: "",
      role: "cashier",
      is_active: true,
    });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name,
      email: u.email,
      mobile: u.mobile || "",
      password: "",
      role: u.role,
      is_active: u.is_active ?? true,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.name.trim() || !form.email.trim()) {
        toast.error("Name & email required");
        return;
      }
      if (!editing && !form.password) {
        toast.error("Password required");
        return;
      }
      if (saving) return;
      setSaving(true);
      if (editing) {
        const payload = {
          name: form.name,
          mobile: form.mobile || null,
          role: form.role,
          is_active: form.is_active,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success(editing ? "Updated" : "User created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const canManage = me?.role === "owner" || me?.role === "super_admin";

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="users-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Access Control
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            Users & Roles
          </h1>
        </div>
        {canManage && (
          <Button
            data-testid="add-user-btn"
            onClick={openCreate}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={16} className="mr-1.5" /> New user
          </Button>
        )}
      </header>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                User
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Email
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Role
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Status
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em] text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-10 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-10 text-muted-foreground"
                >
                  No users yet.
                </TableCell>
              </TableRow>
            ) : (
              list.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 border border-primary/30 grid place-items-center text-gold">
                        <User size={14} weight="duotone" />
                      </div>
                      <div>
                        <div className="font-medium">{u.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {u.mobile || "—"}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>
                    <span className="text-xs uppercase tracking-wider px-2 py-1 rounded-md bg-secondary border border-border">
                      {u.role.replace("_", " ")}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-xs ${u.is_active === false ? "text-muted-foreground" : "text-gold"}`}
                    >
                      {u.is_active === false ? "Inactive" : "Active"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage && (
                      <>
                        <button
                          onClick={() => openEdit(u)}
                          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground mr-1"
                        >
                          <PencilSimple size={14} />
                        </button>
                        {u.id !== me.id && (
                          <button
                            onClick={() => remove(u)}
                            className="p-1.5 rounded-md hover:bg-destructive/15 text-muted-foreground hover:text-destructive"
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit" : "New"} User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Full name *
              </Label>
              <Input
                data-testid="user-name"
                className="mt-1.5 bg-background border-border"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Email *
              </Label>
              <Input
                data-testid="user-email"
                type="email"
                disabled={!!editing}
                className="mt-1.5 bg-background border-border"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Mobile
              </Label>
              <Input
                className="mt-1.5 bg-background border-border"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                {editing ? "New password (leave blank to keep)" : "Password *"}
              </Label>
              <Input
                data-testid="user-password"
                type="password"
                className="mt-1.5 bg-background border-border"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Role
              </Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger
                  data-testid="user-role"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <span className="text-sm">Active</span>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-user-btn"
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
